import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';
import ContactPhotoUpload from './components/ContactPhotoUpload';
import ContactFormFields from './components/ContactFormFields';
import ContactTagsInput from './components/ContactTagsInput';
import DuplicateDetectionAlert from './components/DuplicateDetectionAlert';
import ImportFromDeviceModal from './components/ImportFromDeviceModal';

const AddEditContact = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    birthday: '',
    company: '',
    jobTitle: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    website: '',
    notes: '',
    avatar: null,
    tags: []
  });

  const [errors, setErrors] = useState({});
  const [duplicateContacts, setDuplicateContacts] = useState([]);

  // Mock existing contact data for editing
  const mockExistingContact = {
    id: 'contact_1',
    firstName: 'John',
    lastName: 'Doe',
    phone: '(555) 123-4567',
    email: 'john.doe@example.com',
    birthday: '1990-05-15',
    company: 'Tech Solutions Inc',
    jobTitle: 'Software Engineer',
    address: '123 Main Street',
    city: 'San Francisco',
    state: 'CA',
    zipCode: '94105',
    website: 'https://johndoe.dev',
    notes: 'Met at tech conference. Interested in React development.',
    avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
    tags: ['Work', 'Developer', 'Networking']
  };

  // Mock duplicate contacts
  const mockDuplicates = [
    {
      id: 'dup_1',
      name: 'John Smith',
      email: 'john.smith@example.com',
      phone: '(555) 123-4567',
      avatar: 'https://randomuser.me/api/portraits/men/2.jpg'
    },
    {
      id: 'dup_2',
      name: 'Johnny Doe',
      email: 'johnny.doe@email.com',
      phone: '(555) 987-6543',
      avatar: 'https://randomuser.me/api/portraits/men/3.jpg'
    }
  ];

  useEffect(() => {
    // Check if editing mode based on URL params or state
    const urlParams = new URLSearchParams(location.search);
    const contactId = urlParams?.get('id');
    
    if (contactId) {
      setIsEditing(true);
      // Load existing contact data
      setFormData(mockExistingContact);
    }
  }, [location]);

  useEffect(() => {
    // Auto-save functionality
    const autoSaveTimer = setTimeout(() => {
      if (hasUnsavedChanges && isEditing) {
        // Auto-save logic here
        console.log('Auto-saving contact...');
      }
    }, 30000); // Auto-save every 30 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [formData, hasUnsavedChanges, isEditing]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
    
    // Clear field error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Duplicate detection for name and email
    if ((field === 'firstName' || field === 'lastName' || field === 'email') && value?.length > 2) {
      setTimeout(() => {
        setDuplicateContacts(mockDuplicates);
        setShowDuplicateAlert(true);
      }, 1000);
    }
  };

  const handlePhotoChange = (photoUrl) => {
    setFormData(prev => ({
      ...prev,
      avatar: photoUrl
    }));
    setHasUnsavedChanges(true);
  };

  const handlePhotoRemove = () => {
    setFormData(prev => ({
      ...prev,
      avatar: null
    }));
    setHasUnsavedChanges(true);
  };

  const handleTagsChange = (tags) => {
    setFormData(prev => ({
      ...prev,
      tags
    }));
    setHasUnsavedChanges(true);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\(\d{3}\) \d{3}-\d{4}$/?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData?.website && !/^https?:\/\/.+/?.test(formData?.website)) {
      newErrors.website = 'Please enter a valid website URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('Saving contact:', formData);
      setHasUnsavedChanges(false);
      
      // Navigate to contact details or list
      navigate('/contact-list', { 
        state: { 
          message: isEditing ? 'Contact updated successfully!' : 'Contact created successfully!',
          type: 'success'
        }
      });
    } catch (error) {
      console.error('Error saving contact:', error);
      setErrors({ general: 'Failed to save contact. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmLeave) return;
    }
    navigate(-1);
  };

  const handleDuplicateIgnore = () => {
    setShowDuplicateAlert(false);
    setDuplicateContacts([]);
  };

  const handleDuplicateMerge = (duplicate) => {
    // Merge logic here
    console.log('Merging with:', duplicate);
    setShowDuplicateAlert(false);
    navigate(`/contact-details?id=${duplicate?.id}`);
  };

  const handleViewDuplicate = (duplicate) => {
    window.open(`/contact-details?id=${duplicate?.id}`, '_blank');
  };

  const handleImportFromDevice = (contacts) => {
    if (contacts?.length === 1) {
      // Import single contact data
      const contact = contacts?.[0];
      setFormData(prev => ({
        ...prev,
        firstName: contact?.name?.split(' ')?.[0] || '',
        lastName: contact?.name?.split(' ')?.slice(1)?.join(' ') || '',
        phone: contact?.phone || '',
        email: contact?.email || '',
        avatar: contact?.avatar || null
      }));
      setHasUnsavedChanges(true);
    } else {
      // Multiple contacts - redirect to bulk import
      navigate('/contact-list', { 
        state: { 
          importedContacts: contacts,
          message: `${contacts?.length} contacts imported successfully!`,
          type: 'success'
        }
      });
    }
  };

  const isFormValid = formData?.firstName?.trim() && formData?.phone?.trim() && Object.keys(errors)?.length === 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <BottomTabNavigation />
      {/* Main Content */}
      <div className="lg:ml-60 pb-16 lg:pb-0">
        {/* Navigation Breadcrumbs */}
        <NavigationBreadcrumbs
          customBreadcrumbs={[
            { label: 'Dashboard', path: '/dashboard', icon: 'Home' },
            { label: 'Contacts', path: '/contact-list', icon: 'Users' },
            { 
              label: isEditing ? `Edit ${formData?.firstName || 'Contact'}` : 'Add Contact', 
              path: '/add-edit-contact', 
              icon: isEditing ? 'Edit' : 'UserPlus' 
            }
          ]}
          onBackClick={handleCancel}
        />

        {/* Header */}
        <div className="sticky top-0 z-100 bg-surface border-b border-border shadow-card">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            <div className="flex items-center space-x-3">
              <Icon 
                name={isEditing ? "Edit" : "UserPlus"} 
                size={24} 
                className="text-primary" 
              />
              <div>
                <h1 className="text-xl font-semibold text-text-primary">
                  {isEditing ? `Edit ${formData?.firstName || 'Contact'}` : 'Add New Contact'}
                </h1>
                {hasUnsavedChanges && (
                  <p className="text-sm text-warning">Unsaved changes</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                onClick={() => setShowImportModal(true)}
                iconName="Download"
                iconPosition="left"
                className="hidden sm:flex"
              >
                Import from Device
              </Button>

              <Button
                variant="outline"
                onClick={handleCancel}
              >
                Cancel
              </Button>

              <Button
                variant="default"
                onClick={handleSave}
                loading={isSaving}
                disabled={!isFormValid}
                iconName="Save"
                iconPosition="left"
              >
                {isSaving ? 'Saving...' : 'Save Contact'}
              </Button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="max-w-2xl mx-auto p-4 lg:p-6 space-y-8">
          {/* General Error */}
          {errors?.general && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <p className="text-error font-medium">{errors?.general}</p>
              </div>
            </div>
          )}

          {/* Duplicate Detection Alert */}
          {showDuplicateAlert && (
            <DuplicateDetectionAlert
              duplicates={duplicateContacts}
              onViewDuplicate={handleViewDuplicate}
              onIgnore={handleDuplicateIgnore}
              onMerge={handleDuplicateMerge}
            />
          )}

          {/* Photo Upload */}
          <div className="text-center">
            <ContactPhotoUpload
              currentPhoto={formData?.avatar}
              onPhotoChange={handlePhotoChange}
              onPhotoRemove={handlePhotoRemove}
            />
          </div>

          {/* Form Fields */}
          <ContactFormFields
            formData={formData}
            onFieldChange={handleFieldChange}
            errors={errors}
          />

          {/* Tags */}
          <ContactTagsInput
            selectedTags={formData?.tags}
            onTagsChange={handleTagsChange}
          />

          {/* Mobile Save Button */}
          <div className="lg:hidden sticky bottom-20 z-90">
            <div className="bg-surface border border-border rounded-lg p-4 shadow-modal">
              <Button
                variant="default"
                onClick={handleSave}
                loading={isSaving}
                disabled={!isFormValid}
                fullWidth
                iconName="Save"
                iconPosition="left"
              >
                {isSaving ? 'Saving Contact...' : 'Save Contact'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Import Modal */}
      <ImportFromDeviceModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportFromDevice}
      />
      {/* Mobile Bottom Navigation */}
      <BottomTabNavigation />
    </div>
  );
};

export default AddEditContact;